import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import GradientButton from '../components/GradientButton'
import PageShell from '../components/PageShell'
import StickyActionBar from '../components/StickyActionBar'
import FormField from '../components/FormField'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import styles from './Tasks.module.css'
import { taskApi } from '../utils/taskApi'
import type { EntityBatchSyncStatus, EntityMutationResult, EntityMutationSyncState } from '../utils/entityMutationSync'
import type { Task, TaskUser } from '../types/task'
import { api } from '../utils/api'
import { getStoredPrincipal } from '../utils/apiAuth'
import { exportTasks, importTasks as importTasksFromFile } from '../utils/storage'
import { syncTasksFromDbIntoLocalStorage, syncTasksFromLocalStorageIntoDb } from '../offline/initOffline'
import { deliverTaskNotificationPlaceholders } from '../utils/taskNotifications'
import {
  findLotByDashboardSelection,
  matchesDashboardLotSelection,
  normalizeDashboardLotSelection
} from '../utils/dashboardLotSelection'
import { useSessionState } from '../hooks/useSessionState'
import { useTwoStepConfirm } from '../hooks/useTwoStepConfirm'
import { getProductionSpeciesLabel } from '../utils/productionSpeciesLabels'
import {
  ensureFarmList,
  findFarmById,
  type WizardFarm,
  type WizardProductionUnit
} from '../utils/farmWizard'
import {
  loadStoredSpecies,
  persistSpeciesSelection,
  normalizeSpeciesKey,
  DEFAULT_PRODUCTION_SPECIES,
  PRODUCTION_SPECIES_PRESETS,
  type ProductionSpeciesKey
} from '../data/productionSpeciesPresets'
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import { isLotActiveForInteraction } from '../utils/lotInteraction'
import { useLots } from '../hooks/useLots'
import { fromInputDate, toInputDate } from '../utils/dateNormalization'
import DateTimeLocalInput from '../components/DateTimeLocalInput'
import { useT, type TranslationKey } from '../i18n'
import { deriveTaskSyncOutcome, type TaskSyncOutcome } from '../utils/taskSyncOutcome'
import { computeSyncBannerVisibility } from '../utils/syncBannerVisibility'

const hasWindow = typeof window !== 'undefined'

const pad2 = (value: number): string => String(value).padStart(2, '0')

const toLocalDateKey = (date: Date): string => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

const toLocalIsoDateKey = (isoZ: string): string => {
  const date = new Date(isoZ)
  if (Number.isNaN(date.getTime())) return isoZ.slice(0, 10)
  return toLocalDateKey(date)
}

const getCurrentMonthKey = (): string => {
  const now = new Date()
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`
}

const parseMonthKey = (value: string): { year: number; monthIndex: number } | null => {
  const match = /^(\d{4})-(\d{2})$/.exec(value.trim())
  if (!match) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null
  return { year, monthIndex: month - 1 }
}

const monthKeyFromDate = (date: Date): string => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}`

const addMonthsToMonthKey = (value: string, delta: number): string => {
  const parsed = parseMonthKey(value)
  const base = parsed ? new Date(parsed.year, parsed.monthIndex, 1) : new Date()
  base.setMonth(base.getMonth() + delta)
  return monthKeyFromDate(base)
}

const collaborativeSelectStyle: React.CSSProperties = {
  width: '100%',
  padding: 8,
  borderRadius: 8,
  border: '1px solid #cbd5f5'
}

type TaskContextMetadata = {
  farmId?: string
  productionUnitId?: string
  speciesKey?: ProductionSpeciesKey
  lotId?: string
}

type TaskFormState = {
  title: string
  description: string
  tags: string
  assigneeId: string
  collaboratorIds: string[]
  parentId: string
  dueAt: string
  reminderEnabled: boolean
  cadenceMinutes: number
  farmId: string
  productionUnitId: string
  speciesKey: ProductionSpeciesKey
  lotId: string
}

const extractTaskContext = (metadata?: Record<string, unknown>): TaskContextMetadata => {
  if (!metadata) return {}
  const farmValue = metadata['farmId']
  const unitValue = metadata['productionUnitId']
  const speciesValue = metadata['speciesKey']
  const lotValue = metadata['lotId']
  const farmId = typeof farmValue === 'string' ? farmValue : undefined
  const productionUnitId = typeof unitValue === 'string' ? unitValue : undefined
  const speciesKey = typeof speciesValue === 'string' ? normalizeSpeciesKey(speciesValue) : undefined
  const lotId = typeof lotValue === 'string' ? lotValue : undefined
  return { farmId, productionUnitId, speciesKey, lotId }
}

const mergeTaskMetadata = (
  base: Record<string, unknown> | undefined,
  context: TaskContextMetadata
): Record<string, unknown> | undefined => {
  const next: Record<string, unknown> = { ...(base ?? {}) }
  const assign = (
    key: 'farmId' | 'productionUnitId' | 'speciesKey' | 'lotId',
    value: string | ProductionSpeciesKey | undefined
  ) => {
    if (!value) {
      delete next[key]
    } else {
      next[key] = value
    }
  }

  assign('farmId', context.farmId)
  assign('productionUnitId', context.productionUnitId)
  assign('speciesKey', context.speciesKey)
  assign('lotId', context.lotId)

  return Object.keys(next).length > 0 ? next : undefined
}

const defaultFormState = (): TaskFormState => ({
  title: '',
  description: '',
  tags: '',
  assigneeId: '',
  collaboratorIds: [],
  parentId: '',
  dueAt: '',
  reminderEnabled: true,
  cadenceMinutes: 1440,
  farmId: '',
  productionUnitId: '',
  speciesKey: loadStoredSpecies(),
  lotId: ''
})

const DRAFT_TIMESTAMP = '1970-01-01T00:00:00.000Z'

const parseTagsInput = (value?: string): string[] => {
  const cleaned = (value ?? '')
    .split(',')
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean)
  return Array.from(new Set(cleaned))
}

const formatTagsInput = (tags?: string[]): string => (tags && tags.length > 0 ? tags.join(', ') : '')

const buildTaskFromForm = (
  form: TaskFormState,
  base?: Task,
  context?: TaskContextMetadata
): Task => {
  const metadata = mergeTaskMetadata(base?.metadata, {
    farmId: context?.farmId ?? (form.farmId || undefined),
    productionUnitId: context?.productionUnitId ?? (form.productionUnitId || undefined),
    speciesKey: context ? context.speciesKey : form.speciesKey,
    lotId: context?.lotId ?? (form.lotId || undefined)
  })
  const tags = parseTagsInput(form.tags)

  return {
    id: base?.id ?? '__draft__',
    title: form.title,
    description: form.description?.trim() ? form.description : undefined,
    tags: tags.length > 0 ? tags : undefined,
    assigneeId: form.assigneeId || undefined,
    collaboratorIds: Array.from(new Set(form.collaboratorIds)),
    dueAt: form.dueAt ? fromInputDate(form.dueAt) : undefined,
    completed: base?.completed ?? false,
    completedAt: base?.completedAt,
    createdAt: base?.createdAt ?? DRAFT_TIMESTAMP,
    updatedAt: base?.updatedAt ?? DRAFT_TIMESTAMP,
    parentId: form.parentId || undefined,
    reminderSettings: {
      enabled: form.reminderEnabled,
      cadenceMinutes: form.cadenceMinutes,
      remindCollaborators: form.collaboratorIds.length > 0,
      remindResponsible: true
    },
    lastReminderAt: base?.lastReminderAt,
    lastNotifiedAt: base?.lastNotifiedAt,
    metadata
  }
}

const mergeCandidateIntoTasks = (candidate: Task, tasks: Task[]): Task[] => {
  const index = tasks.findIndex((item) => item.id === candidate.id)
  if (index === -1) {
    return [...tasks, candidate]
  }
  const next = tasks.slice()
  next[index] = candidate
  return next
}

const cadenceOptions: Array<{ labelKey: TranslationKey; value: number }> = [
  { labelKey: 'tasks.cadence.every30min', value: 30 },
  { labelKey: 'tasks.cadence.every2h', value: 120 },
  { labelKey: 'tasks.cadence.every12h', value: 720 },
  { labelKey: 'tasks.cadence.every24h', value: 1440 },
  { labelKey: 'tasks.cadence.every3d', value: 4320 }
]

const ReminderSummary: React.FC<{ task: Task }> = ({ task }) => {
  const t = useT()
  if (!task.reminderSettings.enabled) {
    return <span style={{ color: '#6b7280' }}>{t('tasks.reminder.off')}</span>
  }
  const cadence = task.reminderSettings.cadenceMinutes
  const option = cadenceOptions.find((item) => item.value === cadence)
  return (
    <span style={{ color: '#0f766e' }}>
      {t('tasks.reminder.cadencePrefix')} {option ? t(option.labelKey) : `${cadence} ${t('tasks.due.minutes')}`}
    </span>
  )
}

const formatDueLabel = (t: (key: TranslationKey) => string, iso?: string): { label: string; tone: string } => {
  if (!iso) return { label: t('tasks.due.none'), tone: '#6b7280' }
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return { label: t('tasks.due.invalid'), tone: '#b91c1c' }

  const now = Date.now()
  const diffMs = date.getTime() - now
  const diffMinutes = Math.round(diffMs / 60000)
  if (diffMinutes < -1440) {
    const days = Math.abs(Math.round(diffMinutes / 1440))
    return { label: `${t('tasks.due.overdueByPrefix')} ${days} ${t('tasks.due.days')}`, tone: '#b91c1c' }
  }
  if (diffMinutes < 0) {
    const minutes = Math.abs(diffMinutes)
    return { label: `${t('tasks.due.overdueByPrefix')} ${minutes} ${t('tasks.due.minutes')}`, tone: '#b91c1c' }
  }
  if (diffMinutes < 60) {
    return { label: `${t('tasks.due.dueInPrefix')} ${diffMinutes} ${t('tasks.due.minutes')}`, tone: '#b45309' }
  }
  if (diffMinutes < 1440) {
    return { label: `${t('tasks.due.dueInPrefix')} ${Math.round(diffMinutes / 60)} ${t('tasks.due.hours')}`, tone: '#b45309' }
  }
  return { label: `${t('tasks.due.dueOnPrefix')} ${date.toLocaleString()}`, tone: '#2563eb' }
}

const isOverdue = (iso?: string): boolean => {
  if (!iso) return false
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return false
  return date.getTime() < Date.now()
}

export default function Tasks() {
  const t = useT()
  const location = useLocation()
  const [tasks, setTasks] = useState<Task[]>([])
  const [users, setUsers] = useState<TaskUser[]>([])
  const [principal, setPrincipal] = useState<{ tenantKey: string; role: 'viewer' | 'contributor' | 'editor' | 'admin'; actorId: string | null } | null>(() => {
    const stored = getStoredPrincipal()
    return stored
      ? { tenantKey: stored.tenantKey, role: stored.role, actorId: stored.actorId }
      : null
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [taskMutationStatusMessage, setTaskMutationStatusMessage] = useState<string | null>(null)
  const [connectivityNotice, setConnectivityNotice] = useState<string | null>(null)
  const [taskSyncStatus, setTaskSyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  const [taskSyncLastOutcome, setTaskSyncLastOutcome] = useState<(TaskSyncOutcome & { attemptedAtIso: string }) | null>(null)
  const [refreshingTasks, setRefreshingTasks] = useState(false)
  const [retryingTaskSync, setRetryingTaskSync] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  // v2.1 finding #83: iOS Chrome silently suppresses `window.confirm()` in
  // some event-handler contexts (WebKit dialog gating). Migrated to
  // `useTwoStepConfirm` (src/hooks/useTwoStepConfirm.ts) — two-step in-app
  // confirm pattern established in LotCostSection.tsx:142 and adopted in
  // Feed.tsx and Biomass.tsx α3b.
  const deleteConfirm = useTwoStepConfirm()
  const taskMutationStatusVersionRef = useRef(0)
  // v2.1 finding #61 (WSL-09, 2026-07-01): forward-ref indirection so
  // `applyTaskMutationStatus` (declared below) can trigger a post-drain
  // outbox-status refresh from its `syncPromise.then` callback without a
  // circular dependency on the `syncTaskOutboxStatus` helper declared much
  // later in the file. The `useEffect` below `syncTaskOutboxStatus` keeps
  // the ref pointed at a closure that re-reads the current `tasks` state.
  const refreshTaskSyncStatusFnRef = useRef<() => Promise<void>>(async () => {})

  const [storedViewMode, setStoredViewMode] = useSessionState<string>('tasks.viewMode', 'list')
  const viewMode = storedViewMode === 'month'
    ? 'month'
    : (storedViewMode === 'agenda' || storedViewMode === 'calendar')
      ? 'agenda'
      : 'list'
  const setViewMode = (next: 'list' | 'agenda' | 'month') => setStoredViewMode(next)

  const [monthCursor, setMonthCursor] = useSessionState<string>('tasks.monthCursor', getCurrentMonthKey)

  const [availableFarms, setAvailableFarms] = useState<WizardFarm[]>([])
  const [availableProductionUnits, setAvailableProductionUnits] = useState<WizardProductionUnit[]>([])
  const [selectedFarmId, setSelectedFarmId] = useSessionState<string>('tasks.selectedFarmId', '')
  const [selectedProductionUnitId, setSelectedProductionUnitId] = useSessionState<string>('tasks.selectedProductionUnitId', '')
  const [selectedLotId, setSelectedLotId] = useSessionState<string>('tasks.selectedLotId', '')
  const [speciesKey, setSpeciesKey] = useSessionState<ProductionSpeciesKey>('tasks.speciesKey', () => loadStoredSpecies())
  const [hasHydratedContext, setHasHydratedContext] = useState(false)
  const initialActiveContext = useMemo(() => loadActiveContextSelection(), [])
  const appliedInitialLotRef = useRef(false)

  const [newTask, setNewTask, resetNewTask] = useSessionState<TaskFormState>('tasks.newTaskForm', defaultFormState)
  const [creating, setCreating] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingDrafts, setEditingDrafts] = useState<Record<string, TaskFormState>>({})
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const [isTouchLayout, setIsTouchLayout] = useState(false)
  const [isCompactLayout, setIsCompactLayout] = useState(false)
  const [flashCreateContext, setFlashCreateContext] = useState(false)
  const flashCreateContextTimeoutRef = useRef<ReturnType<typeof globalThis.setTimeout> | null>(null)
  const [importing, setImporting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const createFormRef = useRef<HTMLElement | null>(null)
  const contextSelectorRef = useRef<HTMLDivElement | null>(null)
  const createParams = useMemo(() => new URLSearchParams(location.search), [location.search])
  const shouldFocusCreate = createParams.get('focus') === 'create'
  const shouldFlashCreateContext = createParams.get('flashContext') === '1'
  const flashMessage = createParams.get('flash')?.trim() || ''
  const prefillTitle = createParams.get('title')?.trim() || ''
  const scrollToCreateForm = useCallback((behavior: ScrollBehavior = 'auto') => {
    if (!hasWindow) return
    requestAnimationFrame(() => {
      createFormRef.current?.scrollIntoView({ behavior, block: 'start' })
    })
  }, [])

  useEffect(() => {
    if (!flashMessage) return

    if (flashMessage === 'warehouseTaskCreated') {
      setStatusMessage(t('warehouse.alertTaskCreated'))
    } else if (flashMessage === 'warehouseReorderTaskExists') {
      setStatusMessage(t('warehouse.alertReorderTaskAlreadyExists'))
    }

    if (!hasWindow) return
    try {
      const href = globalThis.window?.location?.href
      if (!href) return
      const next = new URL(href)
      next.searchParams.delete('flash')
      globalThis.window?.history?.replaceState({}, '', next.toString())
    } catch {
      // ignore
    }
  }, [flashMessage, t])

  // Datatable's mortality-rate CTA (and any other cross-screen "create a
  // task about this" entry point) arrives via ?focus=create&title=... —
  // apply it once into the (session-persisted) draft, then strip the
  // param so navigating back here later doesn't keep re-seeding a title
  // over whatever the operator is actually typing.
  useEffect(() => {
    if (!prefillTitle) return

    setNewTask((prev) => ({ ...prev, title: prefillTitle }))

    if (!hasWindow) return
    try {
      const href = globalThis.window?.location?.href
      if (!href) return
      const next = new URL(href)
      next.searchParams.delete('title')
      globalThis.window?.history?.replaceState({}, '', next.toString())
    } catch {
      // ignore
    }
  }, [prefillTitle, setNewTask])

  const clearTaskMutationStatus = () => {
    taskMutationStatusVersionRef.current += 1
    setTaskMutationStatusMessage(null)
  }

  const getTaskMutationStatusKey = (verb: 'created' | 'updated' | 'deleted', state: EntityMutationSyncState): TranslationKey => {
    if (state === 'synced') return `tasks.status.${verb}Synced` as TranslationKey
    if (state === 'offline') return `tasks.status.${verb}Offline` as TranslationKey
    return `tasks.status.${verb}Local` as TranslationKey
  }

  const applyTaskMutationStatus = (
    verb: 'created' | 'updated' | 'deleted',
    result: Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'>
  ) => {
    const nextVersion = taskMutationStatusVersionRef.current + 1
    taskMutationStatusVersionRef.current = nextVersion
    setTaskMutationStatusMessage(t(getTaskMutationStatusKey(verb, result.syncState)))

    if (!result.syncPromise) return

    void result.syncPromise.then((nextState) => {
      if (taskMutationStatusVersionRef.current !== nextVersion) return

      // v2.1 finding #61 (WSL-09): reconcile the attention-banner outbox
      // snapshot so it flips from 'pending' to 'synced' immediately after
      // the drain. Placed BEFORE the `nextState === result.syncState`
      // early-return so the refresh always fires — mutation-state parity
      // is orthogonal to outbox-drain progress. Without this, finding
      // #58's `showActionMessage` rule keeps the stale attention banner
      // pinned while suppressing the post-save toast — operator-visible
      // regression is "save appears stuck".
      void refreshTaskSyncStatusFnRef.current()

      if (nextState === result.syncState) return
      setTaskMutationStatusMessage(t(getTaskMutationStatusKey(verb, nextState)))
    })
  }

  useEffect(() => {
    if (!shouldFocusCreate) return
    if (loading) return

    if (shouldFlashCreateContext) {
      setFlashCreateContext(true)
      if (flashCreateContextTimeoutRef.current !== null) {
        globalThis.clearTimeout(flashCreateContextTimeoutRef.current)
      }
      flashCreateContextTimeoutRef.current = globalThis.setTimeout(() => {
        setFlashCreateContext(false)
        flashCreateContextTimeoutRef.current = null
      }, 2400)
    }

    scrollToCreateForm('auto')
    globalThis.setTimeout(() => {
      const field = document.getElementById('task-title') as HTMLTextAreaElement | HTMLInputElement | null
      field?.focus()
    }, 200)
  }, [loading, shouldFlashCreateContext, shouldFocusCreate, scrollToCreateForm])

  useEffect(() => {
    persistSpeciesSelection(speciesKey)
  }, [speciesKey])

  useEffect(() => {
    if (!hasWindow) {
      return
    }

    const coarsePointerQuery = window.matchMedia('(pointer: coarse)')
    const compactWidthQuery = window.matchMedia('(max-width: 768px)')

    const handleCoarsePointer = (event: MediaQueryListEvent) => {
      setIsTouchLayout(event.matches)
    }

    const handleCompactWidth = (event: MediaQueryListEvent) => {
      setIsCompactLayout(event.matches)
    }

    setIsTouchLayout(coarsePointerQuery.matches)
    setIsCompactLayout(compactWidthQuery.matches)

    const attach = (mq: MediaQueryList, listener: (event: MediaQueryListEvent) => void) => {
      if (typeof mq.addEventListener === 'function') {
        mq.addEventListener('change', listener)
        return () => mq.removeEventListener('change', listener)
      }
      mq.addListener(listener)
      return () => mq.removeListener(listener)
    }

    const detachCoarse = attach(coarsePointerQuery, handleCoarsePointer)
    const detachCompact = attach(compactWidthQuery, handleCompactWidth)

    return () => {
      detachCoarse()
      detachCompact()
    }
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadFarms = async () => {
      try {
        const farms = await ensureFarmList()
        if (cancelled) return
        setAvailableFarms(farms)

        const activeFarmId = initialActiveContext.farmId
        const nextFarmId = (
          activeFarmId && farms.some((farm) => farm.id === activeFarmId)
            ? activeFarmId
            : (selectedFarmId && farms.some((farm) => farm.id === selectedFarmId))
              ? selectedFarmId
              : (farms[0]?.id ?? '')
        )

        if (nextFarmId && nextFarmId !== selectedFarmId) {
          setSelectedFarmId(nextFarmId)
        }

        const selectedFarm = nextFarmId ? findFarmById(farms, nextFarmId) : undefined
        const units = selectedFarm?.productionUnits ?? []
        const activeUnitId = initialActiveContext.productionUnitId
        const nextUnitId = (
          activeUnitId && units.some((unit) => unit.id === activeUnitId)
            ? activeUnitId
            : (selectedProductionUnitId && units.some((unit) => unit.id === selectedProductionUnitId))
              ? selectedProductionUnitId
              : (units[0]?.id ?? '')
        )

        if (nextUnitId !== selectedProductionUnitId) {
          setSelectedProductionUnitId(nextUnitId)
        }

        setHasHydratedContext(true)
      } catch (error) {
        console.error('Tasks: failed to load farms', error)
      }
    }

    void loadFarms()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!hasHydratedContext) return
    if (!selectedFarmId) return
    persistActiveContextSelection({ farmId: selectedFarmId })
  }, [hasHydratedContext, selectedFarmId])

  useEffect(() => {
    if (!hasHydratedContext) return
    if (!selectedProductionUnitId) return
    persistActiveContextSelection({ productionUnitId: selectedProductionUnitId })
  }, [hasHydratedContext, selectedProductionUnitId])

  useEffect(() => {
    if (!hasHydratedContext) return
    if (!selectedLotId) return
    persistActiveContextSelection({ lotId: selectedLotId })
  }, [hasHydratedContext, selectedLotId])

  const selectedFarm = useMemo(
    () => findFarmById(availableFarms, selectedFarmId),
    [availableFarms, selectedFarmId]
  )

  const farmSpeciesKey = useMemo(() => resolveFarmProductionSpeciesKey(selectedFarm), [selectedFarm])
  const speciesPresetLabel = farmSpeciesKey
    ? getProductionSpeciesLabel(t, farmSpeciesKey)
    : '—'

  const selectedFarmLabel = useMemo(() => {
    if (!selectedFarmId) return t('tasks.filter.allFacilities')
    return selectedFarm?.name ?? selectedFarmId
  }, [selectedFarm, selectedFarmId, t])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!selectedProductionUnitId) {
      return selectedFarm ? t('tasks.filter.allProductionUnits') : '—'
    }
    const selected = availableProductionUnits.find((unit) => unit.id === selectedProductionUnitId)
    return selected?.name ?? selectedProductionUnitId
  }, [availableProductionUnits, selectedFarm, selectedProductionUnitId, t])

  useEffect(() => {
    if (!selectedFarm) return
    const farmSpeciesKey = resolveFarmProductionSpeciesKey(selectedFarm)
    if (farmSpeciesKey && farmSpeciesKey !== speciesKey) {
      setSpeciesKey(farmSpeciesKey)
    }
  }, [selectedFarm, setSpeciesKey, speciesKey])

  useEffect(() => {
    if (!selectedFarm) {
      setAvailableProductionUnits((prev) => (prev.length === 0 ? prev : []))
      if (selectedProductionUnitId) {
        setSelectedProductionUnitId('')
      }
      setSelectedLotId((prev) => (prev ? '' : prev))
      return
    }

    const units = selectedFarm.productionUnits ?? []
    setAvailableProductionUnits((prev) => {
      const sameLength = prev.length === units.length
      const sameOrder = sameLength && prev.every((unit, idx) => unit.id === units[idx]?.id)
      if (sameOrder) {
        return prev
      }
      return units
    })

    if (units.length === 0) {
      if (selectedProductionUnitId) {
        setSelectedProductionUnitId('')
      }
      return
    }

    const isValid = Boolean(selectedProductionUnitId) && units.some((unit) => unit.id === selectedProductionUnitId)
    if (!isValid) {
      const activeUnitId = initialActiveContext.productionUnitId
      const nextUnitId = (
        activeUnitId && units.some((unit) => unit.id === activeUnitId)
          ? activeUnitId
          : (units[0]?.id ?? '')
      )
      if (nextUnitId !== selectedProductionUnitId) {
        setSelectedProductionUnitId(nextUnitId)
      }
    }
  }, [initialActiveContext.productionUnitId, selectedFarm, selectedProductionUnitId, setSelectedProductionUnitId, setSelectedLotId])

  const {
    lots: availableLots,
    loading: lotsLoading,
    refresh: refreshLots
  } = useLots({
    farmId: selectedFarmId || undefined,
    productionUnitId: selectedProductionUnitId || undefined,
    enabled: Boolean(selectedFarmId && selectedProductionUnitId)
  })

  const selectedLot = useMemo(
    () => findLotByDashboardSelection(selectedLotId, availableLots),
    [availableLots, selectedLotId]
  )
  const selectedLotIsInteractive = selectedLot ? isLotActiveForInteraction(selectedLot.stage) : false

  const selectedLotLabel = useMemo(() => {
    if (!selectedLotId) {
      if (!selectedProductionUnitId) return '—'
      return t('tasks.filter.allLots')
    }
    return selectedLot?.lotKey || selectedLot?.id || selectedLotId
  }, [selectedLot, selectedLotId, selectedProductionUnitId, t])

  useEffect(() => {
    if (!hasHydratedContext) return

    // Pre-existing race: this used to mark itself "applied" the instant
    // context hydrated, even when farm/PU hadn't resolved yet and
    // `useLots` hadn't had a chance to fetch — `availableLots` was still
    // `[]`, normalization always failed, and the one real seed attempt was
    // silently burned for the rest of the mount (surfaced building the
    // Datatable mortality-rate CTA, which relies on this to actually land
    // the lot on a freshly created task). `!lotsLoading` alone isn't
    // enough to prove `availableLots` is real data — confirmed live via
    // logging that `useLots` briefly reports `loading: false` with a
    // still-empty `lots: []` in the one render right after farmId/PU
    // change the query key, before its own effect flips `loading: true`
    // for the real fetch. Requiring a non-empty roster (a farm with zero
    // lots has nothing to seed anyway, so waiting forever there is
    // harmless) sidesteps that transient window entirely instead of
    // trying to out-time it.
    const lotsReadyToCheck = Boolean(selectedFarmId && selectedProductionUnitId) && !lotsLoading && availableLots.length > 0

    if (!appliedInitialLotRef.current && lotsReadyToCheck) {
      appliedInitialLotRef.current = true
      const activeLotId = initialActiveContext.lotId
      const normalizedActiveLotId = activeLotId
        ? normalizeDashboardLotSelection(activeLotId, availableLots)
        : ''
      if (!selectedLotId && normalizedActiveLotId) {
        setSelectedLotId(normalizedActiveLotId)
        return
      }
    }

    setSelectedLotId((prev) => {
      if (!prev) return ''
      return normalizeDashboardLotSelection(prev, availableLots)
    })
  }, [availableLots, hasHydratedContext, initialActiveContext.lotId, lotsLoading, selectedFarmId, selectedLotId, selectedProductionUnitId, setSelectedLotId])

  useEffect(() => {
    setNewTask((prev) => {
      if (
        prev.speciesKey === speciesKey &&
        prev.farmId === selectedFarmId &&
        prev.productionUnitId === selectedProductionUnitId &&
        prev.lotId === selectedLotId
      ) {
        return prev
      }
      return {
        ...prev,
        speciesKey,
        farmId: selectedFarmId,
        productionUnitId: selectedProductionUnitId,
        lotId: selectedLotId
      }
    })
  }, [speciesKey, selectedFarmId, selectedProductionUnitId, selectedLotId, setNewTask])

  const taskContext = useMemo<TaskContextMetadata>(() => ({
    farmId: selectedFarmId || undefined,
    productionUnitId: selectedProductionUnitId || undefined,
    speciesKey: selectedFarmId ? (farmSpeciesKey ?? speciesKey) : undefined,
    lotId: selectedLotId || undefined
  }), [selectedFarmId, selectedProductionUnitId, selectedLotId, speciesKey, farmSpeciesKey])

  const issueMessage = (issue: { message: string; messageKey?: Parameters<typeof t>[0]; messageParams?: Parameters<typeof t>[1] }) =>
    issue.messageKey ? t(issue.messageKey, issue.messageParams) : issue.message

  const newTaskCandidate = useMemo(() => buildTaskFromForm(newTask, undefined, taskContext), [newTask, taskContext])
  const newTaskValidation = useMemo(
    () => taskApi.validateDraft(newTaskCandidate, { tasks: mergeCandidateIntoTasks(newTaskCandidate, tasks) }),
    [newTaskCandidate, tasks]
  )
  const newTaskErrorFor = (field: 'title' | 'description' | 'assigneeId' | 'dueAt' | 'parentId' | 'general') =>
    (() => {
      const issue = newTaskValidation.issues.find((candidate) => candidate.field === field && candidate.severity === 'error')
      return issue ? issueMessage(issue) : undefined
    })()
  const newTaskWarnings = newTaskValidation.issues
    .filter((issue) => issue.severity === 'warning')
    .map((issue) => issueMessage(issue))

  const userMap = useMemo(() => {
    const map = new Map<string, TaskUser>()
    const canonicalIdMap = new Map<string, string>()
    users.forEach((user) => {
      map.set(user.id, user)
      canonicalIdMap.set(user.id, user.id)
      ;(user.aliasIds ?? []).forEach((aliasId) => {
        if (!aliasId) return
        map.set(aliasId, user)
        canonicalIdMap.set(aliasId, user.id)
      })
    })
    return {
      byId: map,
      canonicalIdMap
    }
  }, [users])

  const resolveCanonicalUserId = useCallback((value?: string | null) => {
    if (!value) return ''
    return userMap.canonicalIdMap.get(value) ?? value
  }, [userMap])

  const resolveCanonicalUserIds = useCallback((values: string[]) => {
    const resolved = values
      .map((value) => resolveCanonicalUserId(value))
      .filter((value): value is string => value.length > 0)
    return Array.from(new Set(resolved))
  }, [resolveCanonicalUserId])

  const { childMap, parentMap, rootTasks, descendantMap, taskLookup } = useMemo(() => {
    const map = new Map<string, Task[]>()
    const parentLookup = new Map<string, string>()
    const idSet = new Set(tasks.map((task) => task.id))
    const rootOrder: Task[] = []
    const rootSeen = new Set<string>()
    const lookup = new Map<string, Task>()

    tasks.forEach((task) => {
      lookup.set(task.id, task)
    })

    tasks.forEach((task) => {
      if (task.parentId && idSet.has(task.parentId)) {
        const bucket = map.get(task.parentId) ?? []
        bucket.push(task)
        map.set(task.parentId, bucket)
        parentLookup.set(task.id, task.parentId)
      }
    })

    tasks.forEach((task) => {
      if (!task.parentId || !idSet.has(task.parentId)) {
        if (!rootSeen.has(task.id)) {
          rootSeen.add(task.id)
          rootOrder.push(task)
        }
      }
    })

    const descendantCache = new Map<string, Set<string>>()

    const computeDescendants = (id: string, trail: Set<string> = new Set()): Set<string> => {
      if (descendantCache.has(id)) {
        return descendantCache.get(id)!
      }
      if (trail.has(id)) {
        const empty = new Set<string>()
        descendantCache.set(id, empty)
        return empty
      }
      const nextTrail = new Set(trail)
      nextTrail.add(id)
      const children = map.get(id) ?? []
      const aggregate = new Set<string>()
      children.forEach((child) => {
        aggregate.add(child.id)
        const branch = computeDescendants(child.id, nextTrail)
        branch.forEach((value) => aggregate.add(value))
      })
      descendantCache.set(id, aggregate)
      return aggregate
    }

    tasks.forEach((task) => {
      computeDescendants(task.id)
    })

    return {
      childMap: map,
      parentMap: parentLookup,
      rootTasks: rootOrder,
      descendantMap: descendantCache,
      taskLookup: lookup
    }
  }, [tasks])

  const parentCandidates = useMemo(() =>
    tasks.filter((task) => !task.completed || (childMap.get(task.id)?.length ?? 0) > 0),
  [tasks, childMap])

  const matchSet = useMemo(() => {
    const query = searchTerm.trim().toLowerCase()
    if (!query) return null
    const matches = new Set<string>()
    tasks.forEach((task) => {
      const tagText = (task.tags ?? []).join(' ')
      const assigneeName = task.assigneeId ? (userMap.byId.get(task.assigneeId)?.name ?? '') : ''
      const collaboratorNames = (task.collaboratorIds ?? [])
        .map((id) => userMap.byId.get(id)?.name ?? '')
        .filter(Boolean)
        .join(' ')

      const haystack = `${task.title} ${task.description ?? ''} ${tagText} ${assigneeName} ${collaboratorNames}`.toLowerCase()
      if (haystack.includes(query)) {
        matches.add(task.id)
      }
    })
    return matches
  }, [tasks, searchTerm, userMap])

  const visibleIds = useMemo(() => {
    if (!matchSet) return null
    const visible = new Set<string>()
    matchSet.forEach((id) => {
      visible.add(id)
      const descendants = descendantMap.get(id)
      descendants?.forEach((childId) => visible.add(childId))
    })

    const addAncestors = (id: string, guard: Set<string> = new Set()) => {
      if (guard.has(id)) return
      guard.add(id)
      const parentId = parentMap.get(id)
      if (parentId && !visible.has(parentId)) {
        visible.add(parentId)
        addAncestors(parentId, guard)
      }
    }

    Array.from(visible).forEach((id) => addAncestors(id))
    return visible
  }, [matchSet, descendantMap, parentMap])

  const shouldDisplayTask = useCallback((task: Task) => {
    const context = extractTaskContext(task.metadata)
    if (selectedFarmId && context.farmId && context.farmId !== selectedFarmId) {
      return false
    }
    if (selectedProductionUnitId && context.productionUnitId && context.productionUnitId !== selectedProductionUnitId) {
      return false
    }
    if (selectedLotId && context.lotId && !matchesDashboardLotSelection(selectedLotId, context.lotId, availableLots)) {
      return false
    }
    if (context.speciesKey && context.speciesKey !== speciesKey) {
      return false
    }
    if (!visibleIds) return true
    return visibleIds.has(task.id)
  }, [availableLots, visibleIds, selectedFarmId, selectedProductionUnitId, selectedLotId, speciesKey])

  const hasVisibleTasks = useMemo(() => {
    const visit = (task: Task): boolean => {
      if (shouldDisplayTask(task)) return true
      const children = childMap.get(task.id) ?? []
      return children.some(visit)
    }

    return rootTasks.some(visit)
  }, [childMap, rootTasks, shouldDisplayTask])

  type TaskValidationResult = ReturnType<typeof taskApi.validateDraft>
  type TaskClarityResult = ReturnType<typeof taskApi.evaluateClarity>
  type TaskStatusResult = ReturnType<typeof taskApi.classifyStatus>

  const displayIdSet = useMemo(() => {
    const set = new Set<string>()
    tasks.forEach((task) => {
      if (shouldDisplayTask(task)) {
        set.add(task.id)
      }
    })
    return set
  }, [tasks, shouldDisplayTask])

  const hasVisibleDescendantMap = useMemo(() => {
    const map = new Map<string, boolean>()
    tasks.forEach((task) => {
      const descendants = descendantMap.get(task.id)
      if (!descendants || descendants.size === 0) {
        map.set(task.id, false)
        return
      }

      let hasVisibleDescendant = false
      descendants.forEach((descendantId) => {
        if (!hasVisibleDescendant && displayIdSet.has(descendantId)) {
          hasVisibleDescendant = true
        }
      })

      map.set(task.id, hasVisibleDescendant)
    })
    return map
  }, [tasks, descendantMap, displayIdSet])

  const baseValidationById = useMemo(() => {
    const map = new Map<string, TaskValidationResult>()
    tasks.forEach((task) => {
      map.set(task.id, taskApi.validateDraft(task, { tasks }))
    })
    return map
  }, [tasks])

  const baseClarityById = useMemo(() => {
    const map = new Map<string, TaskClarityResult>()
    tasks.forEach((task) => {
      map.set(task.id, taskApi.evaluateClarity(task.title))
    })
    return map
  }, [tasks])

  const baseStatusById = useMemo(() => {
    const map = new Map<string, TaskStatusResult>()
    tasks.forEach((task) => {
      map.set(task.id, taskApi.classifyStatus(task))
    })
    return map
  }, [tasks])

  const getParentOptions = useCallback((taskId?: string) => {
    if (!taskId) return parentCandidates
    const disallowed = new Set<string>([taskId])
    const descendants = descendantMap.get(taskId)
    descendants?.forEach((id) => disallowed.add(id))
    return parentCandidates.filter((candidate) => !disallowed.has(candidate.id))
  }, [parentCandidates, descendantMap])

  const hasSearchQuery = searchTerm.trim().length > 0

  const filtersActive = useMemo(() => (
    hasSearchQuery ||
    Boolean(selectedFarmId) ||
    Boolean(selectedProductionUnitId) ||
    Boolean(selectedLotId) ||
    speciesKey !== DEFAULT_PRODUCTION_SPECIES
  ), [hasSearchQuery, selectedFarmId, selectedProductionUnitId, selectedLotId, speciesKey])

  useEffect(() => {
    if (!matchSet || matchSet.size === 0) return
    setExpandedGroups((prev) => {
      const next = { ...prev }
      rootTasks.forEach((task) => {
        if (visibleIds?.has(task.id)) {
          next[task.id] = true
        }
      })
      return next
    })
  }, [matchSet, rootTasks, visibleIds])

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const current = prev[groupId] ?? true
      return {
        ...prev,
        [groupId]: !current
      }
    })
  }, [])

  const isViewer = principal?.role === 'viewer'
  const canManageTasks = principal?.role === 'editor' || principal?.role === 'admin'
  const canImportTasks = canManageTasks

  const clarity = taskApi.evaluateClarity(newTask.title)

  const syncTaskOutboxStatus = useCallback(async (items: Task[]) => {
    const taskIds = Array.from(new Set(items.map((task) => task.id).filter(Boolean)))
    if (taskIds.length === 0) {
      setTaskSyncStatus(null)
      return
    }

    const nextStatus = await taskApi.readTaskSyncStatus(taskIds)
    setTaskSyncStatus(nextStatus)
  }, [])

  // v2.1 finding #61 (WSL-09): keep the post-drain refresh ref pointed at
  // a closure that re-reads the CURRENT `tasks` state so
  // `applyTaskMutationStatus` (declared earlier in the file) can call
  // through it from its `syncPromise.then` callback. See the ref's
  // declaration block above for the full rationale.
  useEffect(() => {
    refreshTaskSyncStatusFnRef.current = () => syncTaskOutboxStatus(tasks)
  }, [syncTaskOutboxStatus, tasks])

  const syncTaskList = useCallback(async () => {
    try {
      const items = await taskApi.listTasks()
      setTasks(items)
      await syncTaskOutboxStatus(items)
      const source = taskApi.getLastListSource()
      setConnectivityNotice(source === 'cache'
        ? t('tasks.notice.cachedTasks')
        : null)
    } catch (err) {
      setConnectivityNotice(null)
      setTaskSyncStatus(null)
      throw err
    }
  }, [syncTaskOutboxStatus, t])

  const loadInitial = async () => {
    setLoading(true)
    setError(null)
    try {
      await syncTaskList()
      const [roster, me] = await Promise.all([
        taskApi.listUsers(),
        api.me.get().catch(() => null)
      ])
      setUsers(roster)
      if (me) {
        setPrincipal({ tenantKey: me.tenantKey, role: me.role, actorId: me.actorId })
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : t('tasks.error.unableToLoad')
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadInitial()
  }, [])

  const refreshTasks = async (options?: { showBusy?: boolean }) => {
    if (options?.showBusy) {
      setRefreshingTasks(true)
    }
    try {
      await syncTaskList()
    } finally {
      if (options?.showBusy) {
        setRefreshingTasks(false)
      }
    }
  }

  const handleNewInput = <K extends keyof TaskFormState>(key: K, value: TaskFormState[K]) => {
    setNewTask((prev) => ({
      ...prev,
      [key]: value
    }))
  }

  const handleCollaboratorSelect = (event: React.ChangeEvent<HTMLSelectElement>, formUpdater: (ids: string[]) => void) => {
    const options = Array.from(event.target.selectedOptions)
    formUpdater(options.map((option) => option.value))
  }

  const handleClearFilters = useCallback(() => {
    setSpeciesKey(DEFAULT_PRODUCTION_SPECIES)
    setSelectedFarmId('')
    setSelectedProductionUnitId('')
    setSelectedLotId('')
    setSearchTerm('')
  }, [setSelectedFarmId, setSelectedProductionUnitId, setSelectedLotId, setSpeciesKey, setSearchTerm])

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault()

    if (principal?.role === 'viewer') {
      setError(t('tasks.error.readonlyCreate'))
      return
    }

    if (selectedLotId && (!selectedLot || !selectedLotIsInteractive)) {
      setError(t('tasks.error.activeLotRequired'))
      return
    }

    const candidate = buildTaskFromForm(newTask, undefined, taskContext)
    const validation = taskApi.validateDraft(candidate, { tasks: mergeCandidateIntoTasks(candidate, tasks) })
    if (validation.hasErrors) {
      const message = validation.issues
        .filter((issue) => issue.severity === 'error')
        .map((issue) => issueMessage(issue))
        .join(' ') || t('tasks.error.resolveBeforeSaving')
      setError(message)
      return
    }
    setError(null)
    setStatusMessage(null)
    clearTaskMutationStatus()
    setCreating(true)
    try {
      const result = await taskApi.createTask({
        title: newTask.title,
        description: newTask.description || undefined,
        tags: candidate.tags,
        assigneeId: newTask.assigneeId || undefined,
        collaboratorIds: newTask.collaboratorIds,
        parentId: newTask.parentId || undefined,
        dueAt: fromInputDate(newTask.dueAt),
        reminderSettings: {
          enabled: newTask.reminderEnabled,
          cadenceMinutes: newTask.cadenceMinutes,
          remindCollaborators: newTask.collaboratorIds.length > 0,
          remindResponsible: true
        },
        metadata: candidate.metadata
      })
      const created = result.data
      applyTaskMutationStatus('created', result)
      await deliverTaskNotificationPlaceholders(created)
      resetNewTask()
      setNewTask((prev) => ({
        ...prev,
        speciesKey,
        farmId: selectedFarmId,
        productionUnitId: selectedProductionUnitId,
        lotId: selectedLotId
      }))
      await refreshTasks()
    } catch (err) {
      clearTaskMutationStatus()
      const message = err instanceof Error ? err.message : t('tasks.error.failedToCreate')
      setError(message)
    } finally {
      setCreating(false)
    }
  }

  const startEditing = (task: Task) => {
      const contextMeta = extractTaskContext(task.metadata)
    setEditingDrafts((prev) => ({
      ...prev,
      [task.id]: {
        title: task.title,
        description: task.description ?? '',
        tags: formatTagsInput(task.tags),
        assigneeId: resolveCanonicalUserId(task.assigneeId),
        collaboratorIds: resolveCanonicalUserIds(task.collaboratorIds.slice()),
        parentId: task.parentId ?? '',
        dueAt: toInputDate(task.dueAt),
        reminderEnabled: task.reminderSettings.enabled,
          cadenceMinutes: task.reminderSettings.cadenceMinutes,
          farmId: contextMeta.farmId ?? '',
          productionUnitId: contextMeta.productionUnitId ?? '',
          speciesKey: contextMeta.speciesKey ?? speciesKey,
          lotId: contextMeta.lotId ?? ''
      }
    }))
    setEditingId(task.id)
  }

  const handleEditChange = <K extends keyof TaskFormState>(taskId: string, key: K, value: TaskFormState[K]) => {
    setEditingDrafts((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [key]: value
      }
    }))
  }

  const cancelEditing = (taskId: string) => {
    setEditingDrafts((prev) => {
      const next = { ...prev }
      delete next[taskId]
      return next
    })
    setEditingId((prev) => (prev === taskId ? null : prev))
  }

  const saveEditing = async (taskId: string) => {
    const draft = editingDrafts[taskId]
    if (!draft) return
    const baseTask = tasks.find((item) => item.id === taskId)
    if (!baseTask) {
      setError(t('tasks.error.taskMissing'))
      return
    }
    const candidate = buildTaskFromForm(draft, baseTask)
    const validation = taskApi.validateDraft(candidate, { tasks: mergeCandidateIntoTasks(candidate, tasks) })
    if (validation.hasErrors) {
      const message = validation.issues
        .filter((issue) => issue.severity === 'error')
        .map((issue) => issueMessage(issue))
        .join(' ') || t('tasks.error.resolveBeforeSaving')
      setError(message)
      return
    }
    const baseMetadataSnapshot = baseTask.metadata ?? {}
    const nextMetadataSnapshot = candidate.metadata ?? {}
    const metadataChanged = JSON.stringify(baseMetadataSnapshot) !== JSON.stringify(nextMetadataSnapshot)
    try {
      clearTaskMutationStatus()
      const result = await taskApi.updateTask({
        id: taskId,
        title: draft.title,
        description: draft.description || null,
        tags: parseTagsInput(draft.tags),
        assigneeId: draft.assigneeId || null,
        collaboratorIds: draft.collaboratorIds,
        dueAt: draft.dueAt ? fromInputDate(draft.dueAt) ?? null : null,
        parentId: draft.parentId ? draft.parentId : null,
        reminderSettings: {
          enabled: draft.reminderEnabled,
          cadenceMinutes: draft.cadenceMinutes,
          remindCollaborators: draft.collaboratorIds.length > 0,
          remindResponsible: true
        },
        metadata: metadataChanged ? (candidate.metadata ? { ...candidate.metadata } : {}) : undefined
      })
      const updated = result.data
      applyTaskMutationStatus('updated', result)
      await deliverTaskNotificationPlaceholders(updated, baseTask)
      cancelEditing(taskId)
      await refreshTasks()
    } catch (err) {
      clearTaskMutationStatus()
      const message = err instanceof Error ? err.message : t('tasks.error.failedToUpdate')
      setError(message)
    }
  }

  const toggleCompleted = async (task: Task) => {
    try {
      clearTaskMutationStatus()
      const result = await taskApi.updateTask({
        id: task.id,
        completed: !task.completed
      })
      const updated = result.data
      applyTaskMutationStatus('updated', result)
      await deliverTaskNotificationPlaceholders(updated, task)
      await refreshTasks()
    } catch (err) {
      clearTaskMutationStatus()
      const message = err instanceof Error ? err.message : t('tasks.error.unableToUpdateStatus')
      setError(message)
    }
  }

  const deleteTask = async (task: Task) => {
    if (!deleteConfirm.isPending(task.id)) {
      deleteConfirm.arm(task.id)
      return
    }
    try {
      clearTaskMutationStatus()
      const result = await taskApi.deleteTask(task.id)
      applyTaskMutationStatus('deleted', result)
      await refreshTasks()
    } catch (err) {
      clearTaskMutationStatus()
      const message = err instanceof Error ? err.message : t('tasks.error.failedToDelete')
      setError(message)
    } finally {
      deleteConfirm.reset()
    }
  }

  const agendaBuckets = useMemo(() => {
    const bucketMap = new Map<string, Task[]>()

    tasks.forEach((task) => {
      const isVisible = shouldDisplayTask(task)
      const hasVisibleDescendant = hasVisibleDescendantMap.get(task.id) ?? false
      if (!isVisible && !hasVisibleDescendant) return

      const dueKey = task.dueAt ? toLocalIsoDateKey(task.dueAt) : 'none'
      const bucket = bucketMap.get(dueKey) ?? []
      bucket.push(task)
      bucketMap.set(dueKey, bucket)
    })

    const keys = Array.from(bucketMap.keys())
    keys.sort((a, b) => {
      if (a === 'none') return 1
      if (b === 'none') return -1
      return a.localeCompare(b)
    })

    const formatKeyLabel = (key: string) => {
      if (key === 'none') return t('tasks.due.none')
      const date = new Date(`${key}T00:00:00`)
      return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    }

    return keys.map((key) => {
      const items = bucketMap.get(key) ?? []
      items.sort((left, right) => {
        const leftTime = left.dueAt ? new Date(left.dueAt).getTime() : Number.POSITIVE_INFINITY
        const rightTime = right.dueAt ? new Date(right.dueAt).getTime() : Number.POSITIVE_INFINITY
        if (leftTime !== rightTime) return leftTime - rightTime
        return left.title.localeCompare(right.title)
      })

      return {
        key,
        label: formatKeyLabel(key),
        tasks: items
      }
    })
  }, [hasVisibleDescendantMap, shouldDisplayTask, t, tasks])

  const tasksByLocalDueDateKey = useMemo(() => {
    const map = new Map<string, Task[]>()

    tasks.forEach((task) => {
      const isVisible = shouldDisplayTask(task)
      const hasVisibleDescendant = hasVisibleDescendantMap.get(task.id) ?? false
      if (!isVisible && !hasVisibleDescendant) return

      const key = task.dueAt ? toLocalIsoDateKey(task.dueAt) : 'none'
      const bucket = map.get(key) ?? []
      bucket.push(task)
      map.set(key, bucket)
    })

    map.forEach((bucket) => {
      bucket.sort((left, right) => {
        const leftTime = left.dueAt ? new Date(left.dueAt).getTime() : Number.POSITIVE_INFINITY
        const rightTime = right.dueAt ? new Date(right.dueAt).getTime() : Number.POSITIVE_INFINITY
        if (leftTime !== rightTime) return leftTime - rightTime
        return left.title.localeCompare(right.title)
      })
    })

    return map
  }, [hasVisibleDescendantMap, shouldDisplayTask, tasks])

  const monthCursorParts = useMemo(() => {
    return parseMonthKey(monthCursor) ?? parseMonthKey(getCurrentMonthKey()) ?? { year: new Date().getFullYear(), monthIndex: new Date().getMonth() }
  }, [monthCursor])

  const monthCursorLabel = useMemo(() => {
    const date = new Date(monthCursorParts.year, monthCursorParts.monthIndex, 1)
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
  }, [monthCursorParts.monthIndex, monthCursorParts.year])

  const monthGridCells = useMemo(() => {
    const firstOfMonth = new Date(monthCursorParts.year, monthCursorParts.monthIndex, 1)
    const firstDow = firstOfMonth.getDay() // 0 Sun .. 6 Sat
    const offsetToMonday = (firstDow + 6) % 7
    const start = new Date(monthCursorParts.year, monthCursorParts.monthIndex, 1 - offsetToMonday)

    return Array.from({ length: 42 }, (_, idx) => {
      const date = new Date(start)
      date.setDate(start.getDate() + idx)
      const key = toLocalDateKey(date)
      const inMonth = date.getFullYear() === monthCursorParts.year && date.getMonth() === monthCursorParts.monthIndex
      return { key, date, inMonth }
    })
  }, [monthCursorParts.monthIndex, monthCursorParts.year])

  const renderMonthTaskItem = useCallback((task: Task) => {
    const overdue = isOverdue(task.dueAt)
    const canToggle = canManageTasks
    return (
      <div key={task.id} className={styles.monthTaskItem}>
        <input
          type="checkbox"
          checked={task.completed}
          disabled={!canToggle}
          onChange={() => {
            if (!canToggle) return
            void toggleCompleted(task)
          }}
        />
        <div
          className={styles.monthTaskTitle}
          style={{
            color: task.completed ? '#6b7280' : overdue ? '#b91c1c' : '#111827',
            textDecoration: task.completed ? 'line-through' : 'none'
          }}
          title={task.title}
        >
          {task.title}
        </div>
      </div>
    )
  }, [canManageTasks, toggleCompleted])

  const renderTaskRow = (
    task: Task,
    depth = 0,
    options?: {
      forceCompact?: boolean
      includeChildren?: boolean
    }
  ): React.ReactNode => {
    const compactLayout = options?.forceCompact ?? isCompactLayout
    const includeChildren = options?.includeChildren ?? true
    const isVisible = shouldDisplayTask(task)
    const children = childMap.get(task.id) ?? []

    if (!isVisible && !(hasVisibleDescendantMap.get(task.id) ?? false)) {
      return null
    }

    const hasChildren = children.length > 0
    const isExpanded = includeChildren && hasChildren ? expandedGroups[task.id] ?? true : false
    const draft = editingId === task.id ? editingDrafts[task.id] : null
    const candidate = draft ? buildTaskFromForm(draft, task) : task
    const clarityAssess = draft ? taskApi.evaluateClarity(candidate.title) : (baseClarityById.get(task.id) ?? taskApi.evaluateClarity(candidate.title))
    const statusInsight = draft ? taskApi.classifyStatus(candidate) : (baseStatusById.get(task.id) ?? taskApi.classifyStatus(candidate))

    const statusDuration = statusInsight.duration
      ? `${statusInsight.duration.count} ${t(statusInsight.duration.unitKey)}`
      : null
    const statusLabel = statusInsight.labelKey ? t(statusInsight.labelKey) : statusInsight.label
    const statusDescription = statusInsight.descriptionKey
      ? t(statusInsight.descriptionKey, statusDuration ? { duration: statusDuration } : undefined)
      : statusInsight.description
    const validationContext = draft ? mergeCandidateIntoTasks(candidate, tasks) : tasks
    const taskValidation = draft
      ? taskApi.validateDraft(candidate, { tasks: validationContext })
      : (baseValidationById.get(task.id) ?? taskApi.validateDraft(candidate, { tasks: validationContext }))
    const fieldError = (field: 'title' | 'description' | 'assigneeId' | 'dueAt' | 'parentId' | 'general') =>
      taskValidation.issues.find((issue) => issue.field === field && issue.severity === 'error')?.message
    const dueWarnings = taskValidation.issues.filter((issue) => issue.severity === 'warning' && issue.field === 'dueAt')
    const generalError = fieldError('general')
    const effectiveDueIso = candidate.dueAt
    const dueDescriptor = formatDueLabel(t, effectiveDueIso)
    const assignee = candidate.assigneeId ? userMap.byId.get(candidate.assigneeId) : undefined
    const parentObjective = candidate.parentId ? taskLookup.get(candidate.parentId) : undefined
    const displayTags = candidate.tags ?? []
    const indent = depth * 24
    const visibleOpacity = isVisible ? 1 : 0.75

    let completedChildren = 0
    let overdueChildren = 0
    let assignedChildren = 0
    children.forEach((child) => {
      if (child.completed) {
        completedChildren += 1
        return
      }
      if (child.assigneeId) {
        assignedChildren += 1
      }
      if (isOverdue(child.dueAt)) {
        overdueChildren += 1
      }
    })
    const activeChildren = children.length - completedChildren

    const summaryParts: string[] = []
    if (hasChildren) {
      summaryParts.push(
        `${children.length} ${t(children.length === 1 ? 'tasks.groupSummary.linkedTaskSingularSuffix' : 'tasks.groupSummary.linkedTaskPluralSuffix')}`
      )
      if (overdueChildren > 0) {
        summaryParts.push(`${overdueChildren} ${t(overdueChildren === 1 ? 'tasks.groupSummary.overdueSingularSuffix' : 'tasks.groupSummary.overduePluralSuffix')}`)
      }
      if (activeChildren > 0) {
        summaryParts.push(`${activeChildren} ${t(activeChildren === 1 ? 'tasks.groupSummary.activeSingularSuffix' : 'tasks.groupSummary.activePluralSuffix')}`)
      }
      if (completedChildren > 0) {
        summaryParts.push(`${completedChildren} ${t(completedChildren === 1 ? 'tasks.groupSummary.doneSingularSuffix' : 'tasks.groupSummary.donePluralSuffix')}`)
      }
      if (assignedChildren < children.length) {
        const unassignedCount = children.length - assignedChildren
        summaryParts.push(`${unassignedCount} ${t(unassignedCount === 1 ? 'tasks.groupSummary.unassignedSingularSuffix' : 'tasks.groupSummary.unassignedPluralSuffix')}`)
      }
    }
    const summary = summaryParts.join(' | ')

    const actionButtons = canManageTasks ? (draft ? (
      <>
        <GradientButton
          type="button"
          size="sm"
          variant="success"
          onClick={() => saveEditing(task.id)}
          disabled={taskValidation.hasErrors}
        >
          {t('common.save')}
        </GradientButton>
        <GradientButton
          type="button"
          size="sm"
          variant="secondary"
          onClick={() => cancelEditing(task.id)}
        >
          {t('common.cancel')}
        </GradientButton>
      </>
    ) : (
      <>
        <GradientButton
          type="button"
          size="xxs"
          variant="info"
          onClick={() => startEditing(task)}
          hoverStyle={{
            background: '#cbd5e1',
            border: '1px solid #94a3b8'
          }}
          style={{
            background: '#e2e8f0',
            color: '#1f2937',
            boxShadow: 'none',
            border: '1px solid #cbd5e1'
          }}
        >
          {t('common.edit')}
        </GradientButton>
        <GradientButton
          type="button"
          size="xxs"
          variant="danger"
          onClick={() => deleteTask(task)}
          hoverStyle={{
            background: '#fecaca',
            border: '1px solid #fca5a5'
          }}
          style={{
            background: '#fee2e2',
            color: '#b91c1c',
            boxShadow: 'none',
            border: '1px solid #fecaca'
          }}
        >
          {deleteConfirm.isPending(task.id) ? t('tasks.confirm.deleteCta') : t('common.delete')}
        </GradientButton>
      </>
    )) : null

    const headerContent = (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        {includeChildren && hasChildren && (
          <button
            type="button"
            onClick={() => toggleGroup(task.id)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              border: '1px solid #cbd5f5',
              backgroundColor: '#eef2ff',
              color: '#312e81',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
            aria-label={isExpanded ? t('tasks.group.collapseAria') : t('tasks.group.expandAria')}
          >
            {isExpanded ? '-' : '+'}
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {draft ? (
            <div style={{ display: 'grid', gap: 8 }}>
              <textarea
                value={draft.title}
                onChange={(event) => handleEditChange(task.id, 'title', event.target.value)}
                rows={3}
                style={{
                  width: '100%',
                  borderRadius: 8,
                  border: '1px solid #cbd5f5',
                  padding: 10
                }}
              />
              <input
                type="text"
                value={draft.tags}
                onChange={(event) => handleEditChange(task.id, 'tags', event.target.value)}
                placeholder={t('tasks.edit.tagsPlaceholder')}
                style={{
                  width: '100%',
                  borderRadius: 8,
                  border: '1px solid #cbd5f5',
                  padding: 8
                }}
              />
              <div style={{ fontSize: 12, color: '#64748b' }}>
                {t('tasks.form.tagsHint')}
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {viewMode !== 'list' && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '2px 8px',
                      fontSize: 12,
                      fontWeight: 600,
                      borderRadius: 999,
                      border: `1px solid ${statusInsight.palette.border}`,
                      backgroundColor: statusInsight.palette.background,
                      color: statusInsight.palette.color
                    }}
                    title={statusDescription}
                  >
                    {statusLabel}
                  </span>
                )}
                <strong style={{ display: 'block', textDecoration: candidate.completed ? 'line-through' : 'none' }}>
                  {task.title}
                </strong>
              </div>
              {task.description && (
                <span style={{ display: 'block', marginTop: 4, color: '#4b5563' }}>{task.description}</span>
              )}
              {displayTags.length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {displayTags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        fontSize: 11,
                        borderRadius: 999,
                        border: '1px solid #e2e8f0',
                        background: '#f8fafc',
                        color: '#475569'
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {draft && fieldError('title') && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#b91c1c' }}>{fieldError('title')}</div>
          )}
          <div style={{ marginTop: 8, fontSize: 12, color: clarityAssess.warnings.length ? '#b91c1c' : '#047857' }}>
            {clarityAssess.warnings.length ? clarityAssess.warnings[0] : t('tasks.clarity.clear')}
          </div>
          {draft && generalError && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#b91c1c' }}>{generalError}</div>
          )}
          {summary && !draft && (
            <div style={{ marginTop: 12, fontSize: 12, color: '#4338ca', background: '#eef2ff', borderRadius: 8, padding: '6px 8px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontWeight: 600 }}>{t('tasks.groupInsight.label')}</span>
              <span>{summary}</span>
            </div>
          )}
        </div>

        {actionButtons && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {actionButtons}
          </div>
        )}
      </div>
    )

    const assigneeContent = draft ? (
      <div>
        <select
          value={draft.assigneeId}
          onChange={(event) => handleEditChange(task.id, 'assigneeId', event.target.value)}
          style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #cbd5f5' }}
        >
          <option value="">{t('tasks.form.responsiblePlaceholder')}</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>{user.name}</option>
          ))}
        </select>
        {fieldError('assigneeId') && (
          <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{fieldError('assigneeId')}</div>
        )}
        <select
          multiple
          value={draft.collaboratorIds}
          onChange={(event) => handleCollaboratorSelect(event, (ids) => handleEditChange(task.id, 'collaboratorIds', ids))}
          style={{ ...collaborativeSelectStyle, marginTop: 8 }}
        >
          {users.map((user) => (
            <option key={user.id} value={user.id}>{user.name}</option>
          ))}
        </select>
        <select
          value={draft.parentId}
          onChange={(event) => handleEditChange(task.id, 'parentId', event.target.value)}
          style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #cbd5f5', marginTop: 8 }}
        >
          <option value="">{t('tasks.form.objectivePlaceholder')}</option>
          {getParentOptions(task.id).map((option) => (
            <option key={option.id} value={option.id}>{option.title}</option>
          ))}
        </select>
        {fieldError('parentId') && (
          <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{fieldError('parentId')}</div>
        )}
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div>{assignee ? assignee.name : t('tasks.assignee.unassigned')}</div>
        {task.collaboratorIds.length > 0 && (
          <div style={{ color: '#4b5563', fontSize: 12 }}>
            {t('tasks.collaborators.prefix')}&nbsp;
            {task.collaboratorIds
              .map((id) => userMap.byId.get(id)?.name ?? t('common.unknown'))
              .join(', ')}
          </div>
        )}
        {parentObjective && (
          <div style={{ color: '#4338ca', fontSize: 12 }}>
            {t('tasks.objective.prefix')} {parentObjective.title}
          </div>
        )}
      </div>
    )

    const scheduleContent = draft ? (
      <div>
        <DateTimeLocalInput
          value={draft.dueAt}
          onChangeValue={(nextValue) => {
            handleEditChange(task.id, 'dueAt', toInputDate(nextValue))
          }}
          pickerOnly
          style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #cbd5f5' }}
        />
        {fieldError('dueAt') && (
          <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{fieldError('dueAt')}</div>
        )}
        <div style={{ marginTop: 8 }}>
          <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={draft.reminderEnabled}
              onChange={(event) => handleEditChange(task.id, 'reminderEnabled', event.target.checked)}
            />
            {t('tasks.reminder.on')}
          </label>
        </div>
        <select
          value={draft.cadenceMinutes}
          onChange={(event) => handleEditChange(task.id, 'cadenceMinutes', Number(event.target.value))}
          style={{ width: '100%', padding: 8, borderRadius: 8, border: '1px solid #cbd5f5', marginTop: 8 }}
        >
          {cadenceOptions.map((option) => (
            <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
          ))}
        </select>
        {dueWarnings.length > 0 && !fieldError('dueAt') && (
          <div style={{ marginTop: 4, fontSize: 12, color: '#4338ca' }}>{dueWarnings[0].message}</div>
        )}
      </div>
    ) : (
      <div>
        <span style={{ color: dueDescriptor.tone }}>{dueDescriptor.label}</span>
        <div style={{ marginTop: 6, fontSize: 12 }}>
          <ReminderSummary task={task} />
        </div>
        {dueWarnings.length > 0 && (
          <div style={{ marginTop: 6, fontSize: 12, color: '#4338ca' }}>{dueWarnings[0].message}</div>
        )}
      </div>
    )

    const completionToggle = (
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        <input
          type="checkbox"
          checked={task.completed}
          disabled={!canManageTasks}
          onChange={() => {
            if (!canManageTasks) return
            void toggleCompleted(task)
          }}
        />
        <span style={{ fontSize: 12, color: task.completed ? '#166534' : '#374151' }}>
          {task.completed ? t('tasks.completion.completed') : t('tasks.completion.markDone')}
        </span>
      </label>
    )

    const childNodes = includeChildren && hasChildren && isExpanded
      ? children.map((child) => renderTaskRow(child, depth + 1, options))
      : null

    if (compactLayout) {
      return (
        <div
          key={task.id}
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            padding: 16,
            backgroundColor: '#ffffff',
            marginBottom: 12,
            boxShadow: depth === 0 ? '0 12px 28px rgba(15, 23, 42, 0.08)' : '0 4px 12px rgba(15, 23, 42, 0.05)',
            marginLeft: depth > 0 ? depth * 16 : 0,
            opacity: visibleOpacity
          }}
        >
          {headerContent}
          <div style={{ marginTop: 16, display: 'grid', gap: 14 }}>
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: 4 }}>{t('tasks.table.who')}</div>
              {assigneeContent}
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: 4 }}>{t('tasks.table.when')}</div>
              {scheduleContent}
            </div>
            <div>
              <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#64748b', marginBottom: 4 }}>{t('tasks.table.done')}</div>
              {completionToggle}
            </div>
          </div>
          {childNodes && (
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12, borderLeft: '2px solid #e0e7ff', paddingLeft: 14 }}>
              {childNodes}
            </div>
          )}
        </div>
      )
    }

    return (
      <React.Fragment key={task.id}>
        <tr style={{ backgroundColor: depth === 0 ? '#fff' : '#f9fafb' }}>
          <td
            style={{
              padding: 12,
              verticalAlign: 'top',
              position: 'relative',
              paddingLeft: 12 + indent,
              borderLeft: depth > 0 ? '2px solid #e5e7eb' : undefined,
              opacity: visibleOpacity
            }}
          >
            {headerContent}
          </td>
          <td style={{ padding: 12, verticalAlign: 'top', opacity: visibleOpacity }}>
            {assigneeContent}
          </td>
          <td style={{ padding: 12, verticalAlign: 'top', opacity: visibleOpacity }}>
            {scheduleContent}
          </td>
          <td style={{ padding: 12, verticalAlign: 'top', opacity: visibleOpacity }}>
            {completionToggle}
          </td>
        </tr>
        {childNodes}
      </React.Fragment>
    )
  }

  const createParentOptions = getParentOptions()
  const rootRowNodes = rootTasks.map((task) => renderTaskRow(task))
  const hasRenderableRows = rootRowNodes.some((node) => node !== null && node !== false && node !== undefined)

  const triggerFilePicker = () => {
    if (!canImportTasks) {
      setError(t('tasks.error.readonlyImport'))
      setStatusMessage(null)
      return
    }
    fileInputRef.current?.click()
  }

  const handleExport = async () => {
    try {
      await syncTasksFromDbIntoLocalStorage()
      const { blob, count } = exportTasks()
      if (!hasWindow) {
        setStatusMessage(t('tasks.status.exportBrowserOnly'))
        return
      }
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = `tasks-${new Date().toISOString().replace(/[:.]/g, '-')}.json`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      window.URL.revokeObjectURL(url)
      setError(null)
      setStatusMessage(
        count === 0
          ? t('tasks.status.noTasksToExport')
          : `${t('tasks.status.exportedPrefix')} ${count} ${count === 1 ? t('common.taskSingular') : t('common.taskPlural')}.`
      )
    } catch (err) {
      const message = err instanceof Error ? err.message : t('tasks.error.unableToExport')
      setError(message)
      setStatusMessage(null)
    }
  }

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!canImportTasks) {
      setError(t('tasks.error.readonlyImport'))
      setStatusMessage(null)
      if (event.target) {
        event.target.value = ''
      }
      return
    }
    const file = event.target.files?.[0]
    if (!file) return
    setImporting(true)
    setError(null)
    setStatusMessage(null)
    try {
      const raw = await file.text()
      const { imported, skipped } = importTasksFromFile(raw)
      await syncTasksFromLocalStorageIntoDb()
      await refreshTasks()
      const importedMessage = `${t('tasks.status.importedPrefix')} ${imported} ${imported === 1 ? t('tasks.status.importedTaskSingular') : t('tasks.status.importedTaskPlural')}`
      const skippedMessage = skipped > 0
        ? `${t('tasks.status.skippedPrefix')} ${skipped} ${skipped === 1 ? t('tasks.status.duplicateSingular') : t('tasks.status.duplicatePlural')}`
        : ''
      setStatusMessage(`${importedMessage}${skippedMessage}.`)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('tasks.error.unableToImport')
      setError(message)
    } finally {
      setImporting(false)
      setTimeout(() => {
        if (event.target) {
          event.target.value = ''
        }
      }, 0)
    }
  }

  const taskSyncAttentionMessage = useMemo(() => {
    if (!taskSyncStatus || taskSyncStatus.syncState === 'synced') return null

    if (taskSyncStatus.blockedCount > 0) {
      return taskSyncStatus.errorMessage
        ? t('tasks.sync.blockedWithReason', { count: taskSyncStatus.blockedCount, reason: taskSyncStatus.errorMessage })
        : t('tasks.sync.blocked', { count: taskSyncStatus.blockedCount })
    }

    if (taskSyncStatus.syncState === 'offline') {
      return t('tasks.sync.offline')
    }

    return t('tasks.sync.pending')
  }, [taskSyncStatus, t])

  const handleTaskSyncRetry = useCallback(async () => {
    const taskIds = Array.from(new Set(tasks.map((task) => task.id).filter(Boolean)))
    if (taskIds.length === 0) {
      setTaskSyncStatus(null)
      return
    }

    setRetryingTaskSync(true)
    setError(null)

    try {
      const retryReport = await taskApi.retryTaskSyncStatusWithReport(taskIds)
      const nextStatus = retryReport.status
      const nextOutcome = deriveTaskSyncOutcome({
        status: nextStatus,
        skippedNotFoundCount: retryReport.skippedNotFoundCount
      })

      setTaskSyncStatus(nextStatus)
      setTaskSyncLastOutcome({
        ...nextOutcome,
        attemptedAtIso: new Date().toISOString()
      })

      if (retryReport.skippedNotFoundCount > 0) {
        setTaskMutationStatusMessage(t('tasks.sync.retryResolvedMissing', { count: retryReport.skippedNotFoundCount }))
      } else if (nextStatus.syncState === 'synced') {
        setTaskMutationStatusMessage(t('tasks.sync.retrySynced'))
      } else if (nextStatus.syncState === 'offline') {
        setTaskMutationStatusMessage(t('tasks.sync.retryOffline'))
      } else {
        setTaskMutationStatusMessage(t('tasks.sync.retryPending'))
      }

      await refreshTasks({ showBusy: true })
    } catch (err) {
      const message = err instanceof Error ? err.message : t('tasks.sync.retryFailed')
      setError(message)
    } finally {
      setRetryingTaskSync(false)
    }
  }, [refreshTasks, tasks, t])

  const handleManualTaskRefresh = useCallback(async () => {
    setError(null)
    try {
      const taskIds = Array.from(new Set(tasks.map((task) => task.id).filter(Boolean)))
      await refreshTasks({ showBusy: true })
      const refreshedStatus: EntityBatchSyncStatus = taskIds.length > 0
        ? await taskApi.readTaskSyncStatus(taskIds)
        : {
            syncState: 'synced',
            blockedCount: 0,
            errorMessage: null,
            blockers: []
          }

      const refreshOutcome = deriveTaskSyncOutcome({
        status: refreshedStatus,
        skippedNotFoundCount: 0
      })

      setTaskSyncStatus(refreshedStatus)
      setTaskSyncLastOutcome({
        ...refreshOutcome,
        attemptedAtIso: new Date().toISOString()
      })
      setTaskMutationStatusMessage(t('tasks.sync.refreshComplete'))
    } catch (err) {
      const message = err instanceof Error ? err.message : t('tasks.sync.refreshFailed')
      setError(message)
    }
  }, [refreshTasks, t, tasks])

  const taskSyncLastResultSummary = useMemo(() => {
    if (!taskSyncLastOutcome) return null

    const resultMessage = t(taskSyncLastOutcome.resultKey, taskSyncLastOutcome.resultParams)
    const attemptedAtLabel = new Date(taskSyncLastOutcome.attemptedAtIso).toLocaleString()

    return {
      resultLabel: t('tasks.sync.lastResult', { result: resultMessage }),
      attemptedAtLabel: t('tasks.sync.lastAttempted', { time: attemptedAtLabel })
    }
  }, [taskSyncLastOutcome, t])

  return (
    <PageShell style={{ maxWidth: 1100 }}>
      <div className={styles.page}>
        <div className={styles.section}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ minWidth: 0 }}>
              <h2 className="h2">{t('tasks.title')}</h2>
              <p className="lead">{t('tasks.subtitle')}</p>
            </div>
            {/* v2.1 #27 — always-available per-page Refresh button so the
                operator doesn't have to wait for the attention banner to
                appear (or fall back to a browser refresh). */}
            <GradientButton
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void handleManualTaskRefresh()}
              loading={refreshingTasks}
              disabled={refreshingTasks || retryingTaskSync}
              data-testid="tasks-header-refresh"
            >
              {refreshingTasks ? t('tasks.sync.refreshing') : t('tasks.sync.refreshAction')}
            </GradientButton>
          </div>
        </div>

        {(error || statusMessage || taskMutationStatusMessage || connectivityNotice || taskSyncAttentionMessage || taskSyncLastResultSummary) && (
          <div className={styles.alerts}>
            {error && <div className={`${styles.alert} ${styles.alertError}`}>{error}</div>}
            {statusMessage && <div className={`${styles.alert} ${styles.alertStatus}`}>{statusMessage}</div>}
            {taskMutationStatusMessage && computeSyncBannerVisibility({
              hasActionMessage: Boolean(taskMutationStatusMessage),
              hasAttentionMessage: Boolean(taskSyncAttentionMessage),
              hasLastResultSummary: Boolean(taskSyncLastResultSummary)
            }).showActionMessage && <div className={`${styles.alert} ${styles.alertStatus}`}>{taskMutationStatusMessage}</div>}
            {connectivityNotice && <div className={`${styles.alert} ${styles.alertNotice}`}>{connectivityNotice}</div>}
            {taskSyncLastResultSummary && (
              <div className={`${styles.alert} ${styles.alertNotice}`}>
                <div>{taskSyncLastResultSummary.resultLabel}</div>
                <div>{taskSyncLastResultSummary.attemptedAtLabel}</div>
              </div>
            )}
            {taskSyncAttentionMessage && (
              <div className={`${styles.alert} ${styles.alertNotice}`}>
                <div>{taskSyncAttentionMessage}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <GradientButton
                    type="button"
                    size="sm"
                    variant="info"
                    onClick={() => void handleTaskSyncRetry()}
                    loading={retryingTaskSync}
                    disabled={retryingTaskSync || refreshingTasks}
                  >
                    {retryingTaskSync ? t('tasks.sync.retrying') : t('tasks.sync.retryAction')}
                  </GradientButton>
                  <GradientButton
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => void handleManualTaskRefresh()}
                    loading={refreshingTasks}
                    disabled={refreshingTasks || retryingTaskSync}
                  >
                    {refreshingTasks ? t('tasks.sync.refreshing') : t('tasks.sync.refreshAction')}
                  </GradientButton>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Phase 1 UX fix: the create-task form sits below the (potentially
            long) task list further down this page. Pinning its primary
            action here means submitting it never requires scrolling back
            down past the whole list. Nested inside one sticky wrapper (not
            two independently-sticky bars) so it doesn't fight the
            CurrentContextBar below for the same top:0 offset. */}
        <div style={{ position: 'sticky', top: 0, zIndex: 25, background: '#fff' }}>
          <StickyActionBar sticky={false} style={{ marginBottom: shouldFocusCreate ? 0 : 8 }}>
            <GradientButton
              type="submit"
              form="task-create-form"
              disabled={creating || isViewer || !newTask.title.trim()}
            >
              {creating ? t('tasks.form.saving') : t('tasks.form.addTask')}
            </GradientButton>
          </StickyActionBar>

          {!shouldFocusCreate && (
            <CurrentContextBar
              style={{ position: 'static' }}
              species={{ value: farmSpeciesKey ? speciesPresetLabel : '—' }}
              fields={[
                { label: t('tasks.context.facility'), value: selectedFarmLabel },
                { label: t('tasks.context.productionUnit'), value: selectedProductionUnitLabel },
                { label: t('tasks.context.lot'), value: selectedLotLabel }
              ]}
            />
          )}
        </div>

        <div ref={contextSelectorRef} className={styles.filterRow}>
          <ContextSelectorBar
            farm={{
              label: t('tasks.context.facility'),
              value: selectedFarmId,
              onChange: (value) => {
                setSelectedFarmId(value)
                setSelectedProductionUnitId('')
                setSelectedLotId('')

                const picked = availableFarms.find((farm) => farm.id === value)
                const farmSpeciesKey = resolveFarmProductionSpeciesKey(picked)
                if (farmSpeciesKey) {
                  setSpeciesKey(farmSpeciesKey)
                }
              },
              placeholder: t('tasks.filter.allFacilities'),
              options: availableFarms.map((farm) => ({
                key: farm.id ?? farm.name,
                value: farm.id ?? '',
                label: farm.name
              }))
            }}
            productionUnit={{
              label: t('tasks.context.productionUnit'),
              value: selectedProductionUnitId,
              onChange: (value) => {
                setSelectedProductionUnitId(value)
                setSelectedLotId('')
              },
              disabled: !selectedFarm || availableProductionUnits.length === 0,
              placeholder: !selectedFarm ? t('tasks.filter.allProductionUnits') : t('tasks.filter.allUnitsInFacility'),
              options: availableProductionUnits.map((unit) => ({
                key: unit.id ?? unit.name,
                value: unit.id ?? '',
                label: unit.name
              }))
            }}
            lot={{
              label: t('tasks.context.lot'),
              value: selectedLotId,
              onChange: (value) => setSelectedLotId(value),
              disabled: !selectedProductionUnitId || lotsLoading || availableLots.length === 0,
              placeholder: !selectedProductionUnitId
                ? t('tasks.filter.allLots')
                : lotsLoading
                  ? t('tasks.filter.loadingLots')
                  : availableLots.length === 0
                    ? t('tasks.filter.noLotsForUnit')
                    : t('tasks.filter.allLotsInUnit'),
              options: availableLots.map((lot) => ({
                key: lot.id,
                value: lot.id,
                label: isLotActiveForInteraction(lot.stage)
                  ? (lot.lotKey || `Lot ${lot.id}`)
                  : `${lot.lotKey || `Lot ${lot.id}`} (${t('lots.stage.inactive')})`,
                disabled: !isLotActiveForInteraction(lot.stage)
              }))
            }}
          />
        </div>

        <div className={styles.filterHelper}>
          {t('tasks.speciesPresetPrefix')} {farmSpeciesKey ? speciesPresetLabel : '—'} · {t('tasks.selectionHelperSuffix')}
        </div>

        <section className={styles.section}>
          <div className={styles.sectionHeadingRow}>
            <h3 style={{ margin: 0 }}>{t('tasks.section.taskList')}</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <GradientButton
              type="button"
              size="sm"
              variant={viewMode === 'list' ? 'info' : 'secondary'}
              onClick={() => setViewMode('list')}
            >
              {t('tasks.view.list')}
            </GradientButton>
            <GradientButton
              type="button"
              size="sm"
              variant={viewMode === 'agenda' ? 'info' : 'secondary'}
              onClick={() => setViewMode('agenda')}
            >
              {t('tasks.view.agenda')}
            </GradientButton>
            <GradientButton
              type="button"
              size="sm"
              variant={viewMode === 'month' ? 'info' : 'secondary'}
              onClick={() => setViewMode('month')}
            >
              {t('tasks.view.month')}
            </GradientButton>
            <input
              type="search"
              placeholder={t('tasks.searchPlaceholder')}
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className={styles.selectorInput}
              style={{ width: 220 }}
            />
            <GradientButton
              type="button"
              size="sm"
              variant="secondary"
              onClick={handleClearFilters}
              disabled={!filtersActive}
            >
              {t('tasks.clearFilters')}
            </GradientButton>
            <GradientButton
              type="button"
              size="sm"
              variant="success"
              onClick={handleExport}
            >
              {t('tasks.export')}
            </GradientButton>
            <GradientButton
              type="button"
              size="sm"
              variant="info"
              onClick={triggerFilePicker}
              loading={importing}
              disabled={importing || !canImportTasks}
            >
              {importing ? t('tasks.importing') : t('tasks.import')}
            </GradientButton>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/json"
              onChange={handleFileSelection}
              disabled={!canImportTasks}
              style={{ display: 'none' }}
            />
          </div>
        </div>

        {loading ? (
          <p>{t('tasks.loading')}</p>
        ) : tasks.length === 0 ? (
          <p>{t('tasks.empty.noTasks')}</p>
        ) : !hasVisibleTasks ? (
          <p>{t('tasks.empty.noMatches')}</p>
        ) : (
          viewMode === 'month' ? (
            <div className={styles.taskListFrame} style={{ overflow: 'hidden', padding: 12 }}>
              <div className={styles.monthToolbar}>
                <div style={{ fontWeight: 700 }}>{monthCursorLabel}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <GradientButton
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setMonthCursor((prev) => addMonthsToMonthKey(prev, -1))}
                  >
                    {t('tasks.month.prev')}
                  </GradientButton>
                  <GradientButton
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setMonthCursor(getCurrentMonthKey())}
                  >
                    {t('tasks.month.today')}
                  </GradientButton>
                  <GradientButton
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => setMonthCursor((prev) => addMonthsToMonthKey(prev, 1))}
                  >
                    {t('tasks.month.next')}
                  </GradientButton>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <div className={styles.monthGrid} style={{ minWidth: 780 }}>
                  {[
                    t('tasks.weekday.mon'),
                    t('tasks.weekday.tue'),
                    t('tasks.weekday.wed'),
                    t('tasks.weekday.thu'),
                    t('tasks.weekday.fri'),
                    t('tasks.weekday.sat'),
                    t('tasks.weekday.sun')
                  ].map((label) => (
                    <div key={label} className={styles.monthWeekday}>
                      {label}
                    </div>
                  ))}
                  {monthGridCells.map((cell) => {
                    const items = tasksByLocalDueDateKey.get(cell.key) ?? []
                    return (
                      <div
                        key={cell.key}
                        className={`${styles.monthCell} ${cell.inMonth ? '' : styles.monthCellMuted}`}
                      >
                        <div className={styles.monthCellHeader}>
                          <div className={styles.monthDayNumber}>{cell.date.getDate()}</div>
                        </div>
                        <div className={styles.monthCellBody}>
                          {items.length === 0 ? null : items.map(renderMonthTaskItem)}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>

              {(tasksByLocalDueDateKey.get('none') ?? []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
                    <div style={{ fontWeight: 700 }}>{t('tasks.due.none')}</div>
                    <div className={styles.filterHelper}>
                      {(tasksByLocalDueDateKey.get('none') ?? []).length} {t('common.taskPlural')}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {(tasksByLocalDueDateKey.get('none') ?? []).map(renderMonthTaskItem)}
                  </div>
                </div>
              )}
            </div>
          ) : viewMode === 'agenda' ? (
            <div className={styles.taskListFrame} style={{ overflow: 'visible', padding: 12 }}>
              {agendaBuckets.length === 0 ? (
                <div className={styles.emptyState}>
                  {hasSearchQuery ? t('tasks.empty.searchNoMatch') : t('tasks.empty.adjustFilters')}
                </div>
              ) : (
                agendaBuckets.map((bucket) => (
                  <div key={bucket.key} style={{ marginBottom: 18 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
                      <div style={{ fontWeight: 700 }}>{bucket.label}</div>
                      <div className={styles.filterHelper}>
                        {bucket.tasks.length} {bucket.tasks.length === 1 ? t('common.taskSingular') : t('common.taskPlural')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {bucket.tasks.map((task) => renderTaskRow(task, 0, { forceCompact: true, includeChildren: false }))}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : isCompactLayout ? (
            <div className={styles.taskListFrame} style={{ overflow: 'visible', padding: 12 }}>
              {hasRenderableRows ? (
                rootRowNodes
              ) : (
                <div className={styles.emptyState}>
                  {hasSearchQuery ? t('tasks.empty.searchNoMatch') : t('tasks.empty.adjustFiltersOrGrouping')}
                </div>
              )}
            </div>
          ) : (
            <div className={styles.taskListFrame}>
              <div className={styles.tableViewport}>
                <table className={styles.taskTable}>
                  <thead className={styles.taskTableHead}>
                    <tr>
                      <th style={{ width: '50%' }}>{t('tasks.table.what')}</th>
                      <th style={{ width: '20%' }}>{t('tasks.table.who')}</th>
                      <th style={{ width: '20%' }}>{t('tasks.table.when')}</th>
                      <th style={{ width: '10%' }}>{t('tasks.table.done')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hasRenderableRows ? (
                      rootRowNodes
                    ) : (
                      <tr>
                        <td colSpan={4} className={styles.emptyState}>
                          {hasSearchQuery ? t('tasks.empty.searchNoMatch') : t('tasks.empty.adjustFiltersOrGrouping')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )
        )}

        </section>

        <section
        ref={createFormRef}
        className={`${styles.card}`}
      >
          {shouldFocusCreate && (
            <>
              <div className={styles.createContextHeader}>
                <div className={styles.createContextHeaderLabel}>{t('tasks.create.loggingFor')}</div>
                <button
                  type="button"
                  className={styles.createContextHeaderAction}
                  onClick={() => {
                    contextSelectorRef.current?.scrollIntoView({ behavior: 'auto', block: 'start' })
                  }}
                >
                  {t('tasks.create.changeContext')}
                </button>
              </div>
              <CurrentContextBar
                style={flashCreateContext ? {
                  background: '#eef2ff',
                  boxShadow: '0 0 0 4px rgba(99, 102, 241, 0.18)'
                } : undefined}
                species={{ value: farmSpeciesKey ? speciesPresetLabel : '—' }}
                fields={[
                  { label: t('tasks.context.facility'), value: selectedFarmLabel },
                  { label: t('tasks.context.productionUnit'), value: selectedProductionUnitLabel },
                  { label: t('tasks.context.lot'), value: selectedLotLabel }
                ]}
              />
            </>
          )}
        <h3 style={{ marginTop: 0, marginBottom: 16 }}>{t('tasks.create.title')}</h3>
        {/* v2.1 #27 — mirror the per-mutation toast inline next to the
            create form so the operator sees the confirmation where their
            eye is anchored (the page-top alerts are above the fold and
            easy to miss after submitting). */}
        {taskMutationStatusMessage && (
          <div
            className={`${styles.alert} ${styles.alertStatus}`}
            data-testid="tasks-create-inline-status"
            style={{ marginBottom: 12 }}
          >
            {taskMutationStatusMessage}
          </div>
        )}
        {isViewer && (
          <div className={styles.alert + ' ' + styles.alertNotice} style={{ marginBottom: 12 }}>
            {t('tasks.create.viewerNoticeSignedInAs')} <b>{t('role.viewer')}</b>. {t('tasks.create.viewerNoticeReadOnlyPrefix')} <b>{t('role.contributor')}</b> {t('tasks.create.viewerNoticeOr')} <b>{t('role.editor')}</b> {t('tasks.create.viewerNoticeTokenSuffix')}
          </div>
        )}
        <form id="task-create-form" onSubmit={handleCreate}>
          <div className={styles.selectorGrid}>
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField
                id="task-title"
                label={t('tasks.form.whatLabel')}
                type="textarea"
                rows={3}
                value={newTask.title}
                placeholder={t('tasks.form.whatPlaceholder')}
                onChange={(value) => handleNewInput('title', typeof value === 'string' ? value : String(value))}
                hint={clarity.warnings.length ? clarity.warnings[0] : undefined}
                error={newTaskErrorFor('title')}
                disabled={isViewer}
                required
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField
                id="task-description"
                label={t('tasks.form.additionalContextLabel')}
                type="textarea"
                rows={2}
                value={newTask.description}
                onChange={(value) => handleNewInput('description', typeof value === 'string' ? value : String(value))}
                error={newTaskErrorFor('description')}
                disabled={isViewer}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <FormField
                id="task-tags"
                label={t('tasks.form.tagsLabel')}
                type="text"
                value={newTask.tags}
                placeholder={t('tasks.form.tagsPlaceholder')}
                onChange={(value) => handleNewInput('tags', typeof value === 'string' ? value : String(value))}
                hint={t('tasks.form.tagsHint')}
                disabled={isViewer}
              />
            </div>
            <div>
              <label htmlFor="task-assignee" className={styles.selectorLabel}>{t('tasks.form.responsibleLabel')}</label>
              <select
                id="task-assignee"
                value={newTask.assigneeId}
                onChange={(event) => handleNewInput('assigneeId', event.target.value)}
                disabled={isViewer}
                className={`${styles.selectorInput} ${isViewer ? styles.selectorInputDisabled : ''}`}
              >
                <option value="">{t('tasks.form.responsiblePlaceholder')}</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="task-collaborators" className={styles.selectorLabel}>{t('tasks.form.collaboratorsLabel')}</label>
              <select
                id="task-collaborators"
                multiple
                value={newTask.collaboratorIds}
                onChange={(event) => handleCollaboratorSelect(event, (ids) => handleNewInput('collaboratorIds', ids))}
                disabled={isViewer}
                className={`${styles.collaborativeSelect} ${isViewer ? styles.selectorInputDisabled : ''}`}
              >
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="task-parent" className={styles.selectorLabel}>{t('tasks.form.objectiveLabel')}</label>
              <select
                id="task-parent"
                value={newTask.parentId}
                onChange={(event) => handleNewInput('parentId', event.target.value)}
                disabled={isViewer}
                className={`${styles.selectorInput} ${isViewer ? styles.selectorInputDisabled : ''}`}
              >
                <option value="">{t('tasks.form.objectivePlaceholder')}</option>
                {createParentOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.title}</option>
                ))}
              </select>
              {newTaskErrorFor('parentId') && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{newTaskErrorFor('parentId')}</div>
              )}
              <div className={styles.filterHelper}>
                {t('tasks.form.objectiveHelp')}
              </div>
            </div>
            <div>
              <label htmlFor="task-due" className={styles.selectorLabel}>{t('tasks.form.whenLabel')}</label>
              <DateTimeLocalInput
                id="task-due"
                className={styles.selectorInput}
                disabled={isViewer}
                value={newTask.dueAt}
                onChangeValue={(nextValue) => {
                  handleNewInput('dueAt', toInputDate(nextValue))
                }}
                pickerOnly
              />
              {newTaskErrorFor('dueAt') && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#b91c1c' }}>{newTaskErrorFor('dueAt')}</div>
              )}
              {!newTaskErrorFor('dueAt') && newTaskWarnings.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 12, color: '#4338ca' }}>{newTaskWarnings[0]}</div>
              )}
            </div>
            <div>
              <label htmlFor="task-cadence" className={styles.selectorLabel}>{t('tasks.form.reminderCadenceLabel')}</label>
              <select
                id="task-cadence"
                value={newTask.cadenceMinutes}
                onChange={(event) => handleNewInput('cadenceMinutes', Number(event.target.value))}
                disabled={isViewer}
                className={`${styles.selectorInput} ${isViewer ? styles.selectorInputDisabled : ''}`}
              >
                {cadenceOptions.map((option) => (
                  <option key={option.value} value={option.value}>{t(option.labelKey)}</option>
                ))}
              </select>
              <div style={{ marginTop: 8 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={newTask.reminderEnabled}
                    disabled={isViewer}
                    onChange={(event) => handleNewInput('reminderEnabled', event.target.checked)}
                  />
                  {t('tasks.form.enableReminders')}
                </label>
              </div>
            </div>
          </div>
        </form>
      </section>
    </div>
    </PageShell>
  )
}

